// ApproveInvoiceDialog — confirm approving an invoice before the gated
// mutation runs. Default is approving as yourself; an "on behalf of someone
// else" toggle reveals the approver's name (free text — the contract owner
// may not be an app user) and the date their approval was received (date
// picker capped at local today; the server separately allows one day of UTC
// skew). An optional comment is available on every approval. The confirm
// callback runs the approve; errors render inline and the dialog only
// closes on success.

import { useState } from "react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Alert from "../../common/Alert";
import { formatAud } from "../../../utils/financeCalc";
import { gstSuffix } from "../../../utils/gstSuffix";
import type { ApproveInvoiceInput } from "../../../types/finance";

interface ApproveInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber: string;
  totalAmount: number;
  gstApplies?: boolean;
  /** Runs the approve; the dialog closes itself on success and renders the
   *  thrown error inline on failure. */
  onConfirm: (input: ApproveInvoiceInput) => Promise<unknown>;
  isSubmitting?: boolean;
}

const MAX_COMMENT = 500;
const MAX_NAME = 120;

// Local calendar date (not UTC) — an AEST user's "today" is UTC-tomorrow
// every morning, so a UTC-derived max would block their real today.
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

export default function ApproveInvoiceDialog({
  isOpen,
  onClose,
  invoiceNumber,
  totalAmount,
  gstApplies,
  onConfirm,
  isSubmitting,
}: ApproveInvoiceDialogProps) {
  const [comment, setComment] = useState("");
  const [onBehalf, setOnBehalf] = useState(false);
  const [name, setName] = useState("");
  const [receivedAt, setReceivedAt] = useState(localToday());
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  // Pattern 0: render-time seed guard resets the form on every reopen.
  const [seededFor, setSeededFor] = useState(false);
  if (isOpen && !seededFor) {
    setSeededFor(true);
    setComment("");
    setOnBehalf(false);
    setName("");
    setReceivedAt(localToday());
    setError(null);
    setAttempted(false);
  } else if (!isOpen && seededFor) {
    setSeededFor(false);
  }

  const nameError = onBehalf && !name.trim() ? "Approver name is required" : null;
  const dateError = onBehalf && !receivedAt ? "Date approval received is required" : null;

  const handleConfirm = async () => {
    setAttempted(true);
    if (nameError || dateError) return;
    setError(null);
    const input: ApproveInvoiceInput = {};
    const trimmedComment = comment.trim();
    if (trimmedComment) input.comment = trimmedComment;
    if (onBehalf) input.onBehalf = { name: name.trim(), receivedAt };
    try {
      await onConfirm(input);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve the invoice.");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Approve ${invoiceNumber}?`}>
      <div className="space-y-4">
        <p className="text-body text-slate-600 dark:text-slate-300">
          Approving locks the invoice ({formatAud(totalAmount)} {gstSuffix(gstApplies ?? true)})
          and prepares the payment email for your finance team.
        </p>

        <div>
          <label htmlFor="approve-comment" className="label">
            Comment (optional)
          </label>
          <textarea
            id="approve-comment"
            className="input"
            rows={2}
            maxLength={MAX_COMMENT}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <label className="flex items-center gap-2 text-body text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded-sm border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-900"
            checked={onBehalf}
            onChange={(e) => setOnBehalf(e.target.checked)}
            disabled={isSubmitting}
          />
          Approving on behalf of someone else
        </label>

        {onBehalf && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="approve-behalf-name" className="label">
                Approver name <span className="text-red-600">*</span>
              </label>
              <input
                id="approve-behalf-name"
                type="text"
                className="input"
                maxLength={MAX_NAME}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
                aria-invalid={attempted && !!nameError}
              />
              {attempted && nameError && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">{nameError}</p>
              )}
            </div>
            <div>
              <label htmlFor="approve-behalf-date" className="label">
                Date approval received <span className="text-red-600">*</span>
              </label>
              <input
                id="approve-behalf-date"
                type="date"
                className="input"
                max={localToday()}
                value={receivedAt}
                onChange={(e) => setReceivedAt(e.target.value)}
                disabled={isSubmitting}
                aria-invalid={attempted && !!dateError}
              />
              {attempted && dateError && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">{dateError}</p>
              )}
            </div>
          </div>
        )}

        {error && <Alert variant="error">{error}</Alert>}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isSubmitting} disabled={isSubmitting}>
            Approve invoice
          </Button>
        </div>
      </div>
    </Modal>
  );
}
